import { cn } from '@/shared/lib/cn';
import { WalnutIcon } from '@/shared/ui/WalnutIcon';
import type { TokenBalanceFlag } from '@/shared/hooks/use-account-issue';

interface TokenBalanceBadgeProps {
  /** Already-formatted balance value to render, e.g. "0", "240", "-1200". */
  balance: number | string;
  /**
   * Server-driven flag for the token balance (show-negative, 2026-06-01).
   * `alert` (deep clawback debt) → red capsule, `warning` (running low /
   * depleted-but-grace) → amber capsule, null/undefined (healthy) → plain
   * colored number, no capsule. The server owns the threshold; the client
   * only renders.
   */
  tokensFlag?: TokenBalanceFlag;
  /**
   * Class applied to the wrapper in both states (e.g. font size/weight). On the
   * healthy state it can also set the number color (e.g. `text-muted-foreground`);
   * the capsule state always forces white text on a colored fill regardless.
   */
  className?: string;
}

/**
 * Token balance readout shared across nav surfaces (mobile bottom bar, desktop
 * sidebar). Mirrors the Android pill: when the balance runs low (or is
 * depleted) the number renders as an amber capsule (number first, walnut
 * glyph after) so the warning reads as a status badge, not just a tinted digit.
 * Healthy balances stay a plain number so navigation chrome doesn't shout.
 *
 * The walnut glyph always sits AFTER the number — cross-platform parity.
 */
export function TokenBalanceBadge({
  balance,
  tokensFlag,
  className,
}: TokenBalanceBadgeProps) {
  if (tokensFlag !== 'warning' && tokensFlag !== 'alert') {
    return <span className={cn('tabular-nums whitespace-nowrap', className)}>{balance}</span>;
  }

  // alert (clawback debt) → red, warning (running low) → amber.
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-white tabular-nums',
        tokensFlag === 'alert' ? 'bg-red-500' : 'bg-amber-500',
        className,
      )}
    >
      {balance}
      <WalnutIcon className="h-3.5 w-[11px] shrink-0 text-white" />
    </span>
  );
}
